import {
  AppointmentHistory,
  AppointmentHistoryDetail,
  AppointmentHistoryFilter,
} from './appointmentHistory.models';

export interface AppointmentHistoryState {
  ids: string[];
  proposalEntities: Record<string, AppointmentHistory>;
  proposalDetailsEntities: Record<string, AppointmentHistoryDetail>;
  currentAppointmentFilter: AppointmentHistoryFilter;
  currentAppointmentId: string | null;
  isLoadingAppointmentId: string | null;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
}

export const initAppointmentHistoryState: AppointmentHistoryState = {
  ids: [],
  proposalEntities: {},
  proposalDetailsEntities: {},
  currentAppointmentFilter: {
    searchTerm: '',
    sorting: 'DATE_DESC',
    pageSize: 20,
    searchPeriod: 'LAST_7_DAYS',
    assignedUserIds: [],
    status: [],
    proposalStatus: [],
    includeNonAirshipJobs: false,
  },
  currentAppointmentId: null,
  isLoadingAppointmentId: null,
  isLoading: true,
  isLoaded: false,
  error: null,
};

type Action =
  | {
      type: 'FETCH_APPOINTMENT_HISTORY';
      payload: { filter: Partial<AppointmentHistoryFilter> };
    }
  | {
      type: 'FETCH_APPOINTMENT_HISTORY_SUCCESS';
      payload: {
        appointmentHistory: AppointmentHistory[];
      };
    }
  | { type: 'FETCH_APPOINTMENT_HISTORY_FAILURE'; payload: { error: string } }
  | { type: 'FETCH_APPOINTMENT_DETAIL'; payload: { appointmentId: string } }
  | {
      type: 'FETCH_APPOINTMENT_DETAIL_SUCCESS';
      payload: { appointmentHistoryDetail: AppointmentHistoryDetail };
    }
  | { type: 'FETCH_APPOINTMENT_DETAIL_FAILURE'; payload: { error: string } }
  | {
      type: 'SET_LOADING_APPOINTMENT_ID';
      payload: { appointmentId: string | null };
    }
  | {
      type: 'UPDATE_APPOINTMENT_HISTORY_FILTER';
      payload: { filterOptions: Partial<AppointmentHistoryFilter> };
    }
  | {
      type: 'SET_LOADING_STATE';
      payload: { isLoading: boolean; isLoaded: boolean };
    };

export const appointmentHistoryReducer = (
  state: AppointmentHistoryState,
  action: Action
): AppointmentHistoryState => {
  switch (action.type) {
    case 'FETCH_APPOINTMENT_HISTORY':
      return {
        ...state,
        isLoading: true,
        isLoaded: state.ids.length > 0,
        error: null,
        currentAppointmentFilter: {
          ...state.currentAppointmentFilter,
          ...action.payload.filter,
          pageSize:
            initAppointmentHistoryState.currentAppointmentFilter.pageSize,
        },
      };

    case 'FETCH_APPOINTMENT_HISTORY_SUCCESS': {
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        ids: [
          ...new Set([
            ...state.ids,
            ...action.payload.appointmentHistory.map((t) => t.appointmentId),
          ]),
        ],
        proposalEntities: action.payload.appointmentHistory.reduce(
          (prev, curr) => ({ ...prev, [curr.appointmentId]: curr }),
          state.proposalEntities
        ),
      };
    }

    case 'FETCH_APPOINTMENT_DETAIL':
      return {
        ...state,
        currentAppointmentId: action.payload.appointmentId,
        isLoading: true,
        isLoaded: !!(
          state.proposalEntities[action.payload.appointmentId] ||
          state.proposalDetailsEntities[action.payload.appointmentId]
        ),
        error: null,
      };

    case 'FETCH_APPOINTMENT_DETAIL_SUCCESS': {
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        proposalDetailsEntities: {
          ...state.proposalDetailsEntities,
          [action.payload.appointmentHistoryDetail.appointmentId]:
            action.payload.appointmentHistoryDetail,
        },
      };
    }

    case 'FETCH_APPOINTMENT_HISTORY_FAILURE':
    case 'FETCH_APPOINTMENT_DETAIL_FAILURE':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: action.payload.error,
      };

    case 'SET_LOADING_APPOINTMENT_ID':
      return {
        ...state,
        isLoadingAppointmentId: action.payload.appointmentId,
      };

    case 'UPDATE_APPOINTMENT_HISTORY_FILTER':
      return {
        ...state,
        currentAppointmentFilter: {
          ...state.currentAppointmentFilter,
          ...action.payload.filterOptions,
        },
      };

    case 'SET_LOADING_STATE':
      return { ...state, ...action.payload };

    default:
      return state;
  }
};
